import dataProvider from "@refinedev/simple-rest";
import routerBindings, { DocumentTitleHandler } from "@refinedev/react-router";
import { useNotificationProvider } from "@refinedev/mui";

import GroupIcon from "@mui/icons-material/Group";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import FolderIcon from "@mui/icons-material/Folder";
import HandymanIcon from "@mui/icons-material/Handyman";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import WebIcon from "@mui/icons-material/Web";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import AppSettingsAltIcon from "@mui/icons-material/AppSettingsAlt";
import InventoryIcon from "@mui/icons-material/Inventory";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import DownloadIcon from "@mui/icons-material/Download";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import HandshakeIcon from "@mui/icons-material/Handshake";
import HubIcon from "@mui/icons-material/Hub";

import { api } from "@api/api";
import { appConfig } from "@core/configs/appConfig";
import { authProvider } from "@modules/auth/providers/authProvider";

export const refineConfig = {
  dataProvider: dataProvider(appConfig.api.baseURL, api),
  notificationProvider: useNotificationProvider,
  routerProvider: routerBindings,
  authProvider: authProvider,
  title: "Partiu Admin",
  resources: [
    {
      name: "dashboard",
      list: "/",
      meta: { icon: <DataUsageIcon /> },
    },
    {
      name: "discount_module",
      meta: {
        icon: <FolderIcon />,
      },
    },
    {
      name: "catalog",
      list: "/catalogs",
      meta: {
        parent: "discount_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "category_admin",
      list: "/categorys",
      meta: {
        parent: "discount_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "v1/company_admin",
      list: "/companies",
      create: "/companies/create",
      edit: "/companies/edit/:id",
      meta: {
        parent: "discount_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "v1/store_admin",
      list: "/stores",
      create: "/stores/create",
      edit: "/stores/edit/:id",
      meta: {
        parent: "discount_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "v1/discount_admin",
      list: "/discounts",
      create: "/discounts/create",
      edit: "/discounts/edit/:id",
      meta: {
        parent: "discount_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "limited_code_admin",
      list: "/limited_code",
      meta: {
        parent: "discount_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "activity_admin",
      list: "/activities",
      meta: {
        parent: "discount_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "v1/banner_admin",
      list: "/banners",
      create: "/banners/create",
      edit: "/banners/edit/:id",
      meta: {
        parent: "discount_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "voucher_batch_admin",
      list: "/vouchers",
      meta: {
        parent: "discount_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "library_module",
      meta: {
        icon: <FolderIcon />,
      },
    },
    {
      name: "v1/book_admin",
      list: "/books",
      create: "/books/create",
      edit: "/books/edit/:id",
      meta: {
        parent: "library_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "v1/audiobook_admin",
      list: "/audiobooks",
      meta: {
        parent: "library_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "book_category_admin",
      list: "/book-categories",
      meta: {
        parent: "library_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "book_activity_admin",
      list: "/book-activities",
      meta: {
        parent: "library_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "book_contributor_admin",
      list: "/book-authors",
      meta: {
        parent: "library_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "book_sector_admin",
      list: "/book-sectors",
      meta: {
        parent: "library_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "magazine/magazine_template_admin",
      list: "/magazine-templates",
      meta: {
        parent: "library_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "v2/bookstore/book_month_admin",
      list: "/book-months",
      meta: {
        label: "Períodos Mensais",
        parent: "library_module",
        canDelete: true,
        icon: <CalendarMonthIcon />,
      },
    },
    {
      name: "promocode_module",
      meta: {
        icon: <FolderIcon />,
      },
    },
    {
      name: "promocode_batch_admin",
      list: "/promocode-batches",
      meta: {
        parent: "promocode_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "promocode_admin",
      list: "/promocodes",
      meta: {
        parent: "promocode_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "subscription_module",
      meta: {
        icon: <FolderIcon />,
      },
    },
    {
      name: "level_admin",
      list: "/levels",
      meta: {
        parent: "subscription_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "default_plan_admin",
      list: "/default-plans",
      meta: {
        parent: "subscription_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "subscription_plan_admin",
      list: "/subscription-plan",
      meta: {
        parent: "subscription_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "subscription_admin",
      list: "/subscriptions",
      meta: {
        parent: "subscription_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "subscription_mapintegrationplan_admin",
      list: "/integration-plans",
      meta: {
        parent: "subscription_module",
        canDelete: true,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "erp_integrations",
      list: "/erp-integrations",
      meta: {
        canDelete: false,
        icon: <HubIcon />,
      },
    },
    {
      name: "v1/agreement_admin",
      list: "/agreement",
      create: "/agreement/create",
      edit: "/agreement/edit/:id",
      meta: { canDelete: true, icon: <CardMembershipIcon /> },
    },
    {
      name: "v1/default_product_admin",
      list: "/products",
      create: "/products/create",
      edit: "/products/edit/:id",
      meta: { canDelete: true, icon: <InventoryIcon /> },
    },
    {
      name: "financial_admin",
      list: "/financial",
      meta: {
        canDelete: false,
        icon: <ReceiptLongIcon />,
      },
    },
    /* {
      name: "agreement_plans",
      list: "/agreement_plans",
      create: "/agreement_plans/create",
      edit: "/agreement_plans/edit/:id/",
      meta: { canDelete: true, icon: <CreditCardIcon /> },
    }, */
    {
      name: "notifications_emails_admin",
      list: "/notifications",
      create: "/notifications/create",
      meta: { canDelete: true, icon: <NotificationsActiveIcon /> },
    },
    /* {
      name: "v1/landing_admin",
      list: "/landings",
      create: "/landings/create",
      edit: "/landings/edit/:id",
      meta: { canDelete: true, icon: <WebIcon /> },
    }, */
    /*   {
      name: "flux",
      list: "/flux",
      meta: { icon: <DvrIcon /> },
    }, */
    {
      name: "v1/agreement_app_admin",
      list: "/agreement-apps",
      create: "/agreement-apps/create",
      edit: "/agreement-apps/edit/:id",
      meta: { canDelete: true, icon: <AppSettingsAltIcon /> },
    },
    {
      name: "v1/usage_admin",
      list: "/usages",
      meta: { canDelete: true, icon: <ReceiptLongIcon /> },
    },
    {
      name: "v1/user_admin",
      list: "/users",
      create: "/users/create",
      edit: "/users/edit/:id",
      meta: { canDelete: true, icon: <GroupIcon /> },
    },
    {
      name: "prescriber_admin",
      list: "/prescribers",
      meta: { canDelete: false, icon: <MedicalServicesIcon /> },
    },
    {
      name: "v1/partner_admin",
      list: "/partners",
      create: "/partners/create",
      edit: "/partners/edit/:id",
      meta: {
        canDelete: true,
        icon: <HandshakeIcon />,
      },
    },
    {
      name: "v1/partner_referral_admin",
      list: "/partners?tab=referrals",
      create: "/partners/referrals/create",
      edit: "/partners/referrals/edit/:id",
      meta: {
        // Nested under Parceiros — hide duplicate menu entry.
        hide: true,
        canDelete: true,
        parent: "v1/partner_admin",
      },
    },
    {
      name: "tools",
      meta: {
        icon: <HandymanIcon />,
      },
    },
    {
      name: "referral_admin",
      list: "/referrals",
      meta: { canDelete: false, icon: <GroupIcon /> },
    },
    {
      name: "book_logs_module",
      meta: {
        label: "Logs de Livros",
        icon: <LibraryBooksIcon />,
      },
    },
    {
      name: "v1/received_book_admin",
      list: "/received-books",
      meta: {
        label: "Livros Recebidos",
        parent: "book_logs_module",
        canDelete: false,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "v1/download_book_admin",
      list: "/download-books",
      meta: {
        label: "Downloads de Livros",
        parent: "book_logs_module",
        canDelete: false,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "integration",
      list: "/integration",
      meta: {
        parent: "tools",
        canDelete: false,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "integration_bulk",
      list: "/integration/bulk",
      meta: {
        parent: "tools",
        canDelete: false,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "folder_generator",
      list: "/folder-generator",
      meta: {
        parent: "tools",
        canDelete: false,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "sync_received_books",
      list: "/tools/sync-received-books",
      meta: {
        parent: "tools",
        canDelete: false,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "integration_plan_mapping",
      list: "/integration/plan-mapping",
      meta: {
        parent: "tools",
        canDelete: false,
        icon: <SubdirectoryArrowRightIcon />,
      },
    },
    {
      name: "v1/integration_admin",
      list: "/erp-integrations",
      meta: {
        hide: true,
        canDelete: false,
      },
    },
    {
      name: "v1/agreement_integration_config_admin",
      list: "/erp-integrations?tab=configs",
      meta: {
        hide: true,
        canDelete: true,
      },
    },

  ],

  options: {
    branding: {
      title: "Partiu Admin",
    },
    title: {
      text: "Partiu Admin",
    },
    syncWithLocation: true,
    warnWhenUnsavedChanges: true,
    useNewQueryKeys: true,
    projectId: "PGOirJ-hBYAca-kkSgKA",
  },
};
